// Copyright (c) 2021-22, iPivot, Corp.
'use strict'

const { darken, lighten } = require('polished')

// Glasswing brand violet, on the tint that carries against a dark surface.
const primaryColor = '#A45AE9'
const secondaryColor = '#302e3a'
const dangerColor = '#FF331C'
const successColor = '#3dc643'
const warningColor = '#e5bd32'
const linkColor = '#A45AE9'
const lightFontColor = 'rgba(255, 255, 255, 0.85)'
const darkFontColor = 'rgba(0, 0, 0, 0.85)'

module.exports = {
    variant: 'dark',
    productName: 'Infrastructure View',
    colors: {
        a: {
            color: linkColor,
            hover: {
                color: lighten(0.2, linkColor),
            },
        },
        accordion: {
            background: 'transparent',
            border: '1px solid #302e3a',
            color: lightFontColor,
            iconColor: 'white',
            header: {
                color: lightFontColor,
                open: {
                    background: '#666666',
                    color: lightFontColor,
                },
            },
            item: {
                border: `1px solid ${primaryColor}`,
            },
        },
        alert: {
            danger: {
                background: `${dangerColor}20`,
                border: `1px solid ${dangerColor}`,
                color: lightFontColor,
            },
            primary: {
                background: `${primaryColor}20`,
                border: `1px solid ${primaryColor}`,
                color: lightFontColor,
            },
            secondary: {
                background: `${secondaryColor}20`,
                border: `1px solid ${secondaryColor}`,
                color: lightFontColor,
            },
            success: {
                background: `${successColor}20`,
                border: `1px solid ${successColor}`,
                color: lightFontColor,
            },
            warning: {
                background: `${warningColor}20`,
                border: `1px solid ${warningColor}`,
                color: lightFontColor,
            },
            info: {
                background: '#003236',
                border: '1px solid #008d98',
                color: '#FFFFFF',
            },
        },
        badge: {
            danger: {
                background: '#DD2C00',
                color: '#FFFFFF',
            },
            primary: {
                background: '#0055B7',
                color: '#FFFFFF',
            },
            secondary: {
                background: '#DADADA',
                color: '#000000',
            },
            success: {
                background: '#0ED600',
                color: '#333333',
            },
            warning: {
                background: '#FFC000',
                color: '#393939',
            },
            info: {
                background: '#006E75',
                color: '#FFFFFF',
            },
        },
        body: {
            background: '#151419',
            color: lightFontColor,
            dangerColor,
            successColor,
        },
        dropdown: {
            button: {
                secondary: {
                    active: {
                        background: 'transparent',
                        color: linkColor,
                    },
                },
            },
        },
        button: {
            primary: {
                background: primaryColor,
                border: `1px solid ${primaryColor}`,
                color: lightFontColor,
                boxShadow: 'rgba(27, 31, 35, 0.04) 0 1px 0, rgba(255, 255, 255, 0.25) 0 1px 0 inset',
                active: {
                    background: `${primaryColor}80`,
                    border: `1px solid ${primaryColor}`,
                    color: lightFontColor,
                },
                disabled: {
                    background: `${primaryColor}20`,
                    border: `1px solid ${primaryColor}`,
                    color: lightFontColor,
                },
                hover: {
                    background: `${primaryColor}80`,
                    border: `1px solid ${primaryColor}`,
                    color: lightFontColor,
                    boxShadow: 'rgba(27, 31, 35, 0.04) 0 1px 0, rgba(255, 255, 255, 0.25) 0 1px 0 inset',
                },
            },
            secondary: {
                background: secondaryColor,
                border: `1px solid ${darken(0.15, secondaryColor)}`,
                color: lightFontColor,
                boxShadow: 'rgba(27, 31, 35, 0.04) 0 1px 0, rgba(255, 255, 255, 0.25) 0 1px 0 inset',
                active: {
                    background: `${secondaryColor}80`,
                    border: 'none',
                    boxShadow: 'none',
                    color: lightFontColor,
                },
                disabled: {
                    background: secondaryColor,
                    border: `1px solid ${secondaryColor}`,
                    color: lightFontColor,
                },
                hover: {
                    background: lighten(0.025, secondaryColor),
                    border: `1px solid ${darken(0.15, secondaryColor)}`,
                    color: lightFontColor,
                    boxShadow: 'rgba(27, 31, 35, 0.04) 0 1px 0, rgba(255, 255, 255, 0.25) 0 1px 0 inset',
                },
            },
            light: {
                color: darkFontColor,
            },
            success: {
                background: successColor,
                border: `1px solid ${darken(0.15, successColor)}`,
                color: lightFontColor,
                boxShadow: 'rgba(27, 31, 35, 0.04) 0 1px 0, rgba(255, 255, 255, 0.25) 0 1px 0 inset',
                active: {
                    background: successColor,
                    border: `1px solid ${successColor}`,
                    color: lightFontColor,
                },
                disabled: {
                    background: successColor,
                    border: `1px solid ${successColor}`,
                    color: lightFontColor,
                },
                hover: {
                    background: lighten(0.025, successColor),
                    border: `1px solid ${darken(0.15, successColor)}`,
                    color: lightFontColor,
                    boxShadow: 'rgba(27, 31, 35, 0.04) 0 1px 0, rgba(255, 255, 255, 0.25) 0 1px 0 inset',
                },
            },
            danger: {
                background: dangerColor,
                border: `1px solid ${darken(0.15, dangerColor)}`,
                color: lightFontColor,
                boxShadow: 'rgba(27, 31, 35, 0.04) 0 1px 0, rgba(255, 255, 255, 0.25) 0 1px 0 inset',
                active: {
                    background: dangerColor,
                    border: `1px solid ${dangerColor}`,
                    color: lightFontColor,
                },
                disabled: {
                    background: dangerColor,
                    border: `1px solid ${dangerColor}`,
                    color: lightFontColor,
                },
                hover: {
                    background: lighten(0.025, dangerColor),
                    border: `1px solid ${darken(0.15, dangerColor)}`,
                    color: lightFontColor,
                    boxShadow: 'rgba(27, 31, 35, 0.04) 0 1px 0, rgba(255, 255, 255, 0.25) 0 1px 0 inset',
                },
            },
        },
        buttonGroup: {
            primary: {
                background: 'transparent',
                border: `1px solid ${primaryColor}`,
                color: primaryColor,
                boxShadow: `none`,
                active: {
                    background: primaryColor,
                    border: `1px solid ${primaryColor}`,
                    color: lightFontColor,
                },
                disabled: {
                    background: primaryColor,
                    border: darken(0.1, primaryColor),
                    color: lightFontColor,
                },
                hover: {
                    background: 'transparent',
                    border: `1px solid ${primaryColor}`,
                    color: primaryColor,
                    boxShadow: `0px 0px 0px 2px #E0F7FA`,
                },
            },
            secondary: {
                background: 'transparent',
                border: `1px solid ${secondaryColor}`,
                color: secondaryColor,
                boxShadow: `none`,
                active: {
                    background: secondaryColor,
                    border: `1px solid ${secondaryColor}`,
                    color: lightFontColor,
                },
                disabled: {
                    background: secondaryColor,
                    border: darken(0.1, secondaryColor),
                    color: lightFontColor,
                },
                hover: {
                    background: 'transparent',
                    border: `1px solid ${secondaryColor}`,
                    color: secondaryColor,
                    boxShadow: `0px 0px 0px 2px #E0F7FA`,
                },
            },
        },
        card: {
            background: '#1e1d24',
            border: '1px solid #2c2c2c',
            boxShadow: 'none',
            borderRadius: '3px',
            color: lightFontColor,
            footer: {
                background: '#1e1d24',
                borderTop: '1px solid #2c2c2c',
                color: lightFontColor,
            },
            header: {
                background: '#1e1d24',
                borderBottom: '1px solid #2c2c2c',
                color: lightFontColor,
            },
            hover: {
                boxShadow: `0 0 0 0.2rem ${linkColor}`,
            },
        },
        charts: {
            backgroundColor: '#1e1d24',
            borderColor: '#302e3a',
            textColor: lightFontColor,
            axisColor: lightFontColor,
            tooltip: {
                backgroundColor: '#27252f',
                border: '2px solid #302e3a',
                textColor: lightFontColor,
            },
            gray: { backgroundColor: '#C4C4C4', textColor: darkFontColor },
            blue: { backgroundColor: '#36B6DB', textColor: lightFontColor },
            green: { backgroundColor: '#7EDB64', textColor: lightFontColor },
            orange: { backgroundColor: '#DB9F23', textColor: lightFontColor },
            purple: { backgroundColor: '#7E36DC', textColor: lightFontColor },
            lightPurple: { backgroundColor: '#C8B3E6', textColor: darkFontColor },
            red: { backgroundColor: '#DB2B18', textColor: lightFontColor },
        },
        dropdownMenu: {
            background: '#27252f',
            border: '1px solid #302e3a',
            color: lightFontColor,
            active: {
                background: '#27252f',
                color: lightFontColor,
                fontWeight: '600',
            },
            hover: {
                background: 'transparent',
                color: linkColor,
                fontWeight: '400',
            },
        },
        evidence: {
            backgroundColor: '#1e1d24',
            border: '1px solid #2c2c2c',
            boxShadowColor: '#302e3a',
            color: lightFontColor,
        },
        formCheck: {
            background: primaryColor,
            border: primaryColor,
            color: lightFontColor,
            focus: {
                boxShadow: `0 0 0 2px rgba(0, 176, 198, 0.2)`,
            },
        },
        formControl: {
            backgroundColor: '#27252f',
            border: '1px solid #302e3a',
            boxShadow: `0 0 0 0.15rem ${primaryColor}80`,
            color: lightFontColor,
            error: dangerColor,
            success: successColor,
            disabled: {
                backgroundColor: '#1e1d24',
            },
            // Focus keeps the dark surface. It used to flip to white-on-black, which
            // made every focused input on the login form flash against the theme.
            focus: {
                background: '#27252f',
                border: `1px solid ${primaryColor}`,
                boxShadow: `0 0 0 1px ${primaryColor}`,
                color: lightFontColor,
            },
            isValid: {
                boxShadow: `0 0 0 0.15rem ${successColor}80`,
            },
            isInvalid: {
                boxShadow: `0 0 0 0.15rem ${dangerColor}80`,
            },
        },
        inputGroup: {
            background: '#27252f',
            border: '1px solid #302e3a',
            color: lightFontColor,
        },
        reactSelect: {
            backgroundColor: '#27252f',
            color: lightFontColor,
            borderColor: '#302e3a',
            invalid: {
                boxShadow: `0 0 0 0.15rem ${dangerColor}80`,
            },
            multi: {
                backgroundColor: '#3a3745',
                color: lightFontColor,
            },
            focus: {
                backgroundColor: '#3a3745',
                color: lightFontColor,
            },
            active: {
                backgroundColor: '#666666',
                color: lightFontColor,
            },
            selected: {
                backgroundColor: linkColor,
                color: lightFontColor,
            },
            valid: {
                boxShadow: `0 0 0 0.15rem ${successColor}80`,
            },
        },
        listGroupItem: {
            background: 'transparent',
            borderColor: '#3a3745',
            color: lightFontColor,
        },
        modal: {
            background: '#1e1d24',
            border: '2px solid #302e3a',
            boxShadow: 'none',
            borderRadius: '3px',
            color: lightFontColor,
            closeVariant: 'white',
        },
        motd: {
            backgroundColor: warningColor,
            color: 'white',
            boxShadowColor: '#a9a9a9',
        },
        nav: {
            background: '#0c0b0e',
            borderBottom: 'solid 1px #202020',
            color: lightFontColor,
            hover: {
                color: '#FFFFFF',
            },
            active: {
                background: 'transparent',
                borderBottom: `3px solid ${dangerColor}`,
                fontWeight: '600',
                color: darkFontColor,
            },
            disabled: {
                color: '#9E9E9E',
            },
            dropdownMenu: {
                background: '#151419',
                color: lightFontColor,
                active: {
                    background: '#151419',
                    color: lightFontColor,
                    fontWeight: '600',
                },
                hover: {
                    background: '#151419',
                    color: lightFontColor,
                    fontWeight: '400',
                },
            },
        },
        navPills: {
            background: 'transparent',
            border: `3px solid ${primaryColor}`,
            color: primaryColor,
        },
        pdf: {
            textColor: '#000',
        },
        popover: {
            background: '#27252f',
            border: '2px solid #302e3a',
            color: lightFontColor,
            header: {
                borderBottom: '2px solid #302e3a',
            },
        },
        pre: {
            backgroundColor: '#F5F5F5',
        },
        progressBar: {
            backgroundColor: linkColor,
            color: lightFontColor,
        },
        sideMenu: {
            background: '#1e1d24',
            border: 'none',
            boxShadow: '0px 0px 0px 1px #1e1d24',
            borderRadius: '0px',
            color: lightFontColor,
        },
        systemInfo: {
            background: '#0c0b0e',
            color: lightFontColor,
            button: {
                primary: {
                    background: primaryColor,
                    border: `1px solid ${primaryColor}`,
                },
                secondary: {
                    background: secondaryColor,
                    border: `1px solid ${secondaryColor}`,
                },
            },
        },
        table: {
            background: 'inherit',
            borderColor: '#333333',
            color: lightFontColor,
            input: {
                backgroundColor: 'white',
                borderColor: 'grey',
                boxShadowColor: darkFontColor,
                color: darkFontColor,
            },
            active: {
                backgroundColor: 'blue',
                borderColor: 'blue',
                color: lightFontColor,
            },
            hover: {
                color: lightFontColor,
                backgroundColor: '#27252f',
            },
            highlight: {
                background: primaryColor,
                border: `1px solid rgba(255, 255, 255, 0.9)`,
                color: lightFontColor,
            },
        },
        tabs: {
            background: '#1e1d24',
            borderTop: 'none',
            borderRight: '2px solid #302e3a',
            borderBottom: '2px solid #302e3a',
            borderLeft: '2px solid #302e3a',
            boxShadow: 'none',
            borderRadius: '0px 0px 3px 3px',
            color: lightFontColor,
            active: {
                background: '#1e1d24',
                border: '2px solid #302e3a',
                boxShadow: '-1px 0px 0px 0px #EEEEEE',
                color: lightFontColor,
            },
        },
        text: {
            primary: {
                color: linkColor,
            },
            secondary: {
                color: '#CCCCCC',
            },
            success: {
                color: '#469F4B',
            },
            danger: {
                color: '#FF3300',
            },
            warning: {
                color: '#B78310',
            },
            info: {
                color: '#009CA6',
            },
            muted: {
                color: '#8D8D8D',
            },
        },
    },
    fonts: ['sans-serif', 'Roboto'],
    fontSizes: {
        small: '1em',
        medium: '2em',
        large: '3em',
    },
    images: {
        complianceCertificates: {
            logo: 'glasswing-logo.png',
            companyName: 'Glasswing AI',
            signatureImage: 'brett_signature.png',
            signatoryName: 'Brett Helm',
            signatoryTitle: 'CISO',
            awardedIcon: 'compliance-certificate-awarded-icon.png',
            notAwardedIcon: 'compliance-certificate-not-awarded-icon.png',
        },
        favicon: 'glasswing-favicon.png',
        logoImgAlt: 'Glasswing AI',
        // Standalone brand mark. Themes that set it get the login lockup built from
        // the mark plus live type; themes without one fall back to loginLogo.
        mark: 'glasswing-mark-dark.png',
        logo: 'glasswing-logo.png',
        secondaryLogo: 'glasswing-logo.png',
        loginLogo: 'glasswing-logo.png',
        pdfCoverLogo: 'glasswing-logo-black.png',
    },
}
